/**
 * GovernmentDepartmentDetail — /government/departments/:slug
 *
 * Rich detail page: header, description, officials list, notices,
 * a citizen-message submit form, and an "Instant Website" CTA for
 * the owner.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Landmark, MapPin, Phone, Mail, Globe, Loader2, ShieldCheck,
  Users, Bell, MessageSquare, Sparkles, Video, Send, Clock, AlertTriangle,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import GovernmentCategoryNav from "@/components/GovernmentCategoryNav";
import VideoEmbed from "@/components/VideoEmbed";

export default function GovernmentDepartmentDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { http.get("/government/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    http.get(`/government/departments/${slug}`).then(r => setD(r.data)).catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) return (
    <><GovernmentCategoryNav />
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="font-display text-2xl mb-2">Department not found</h1>
        <p className="opacity-70">{err}</p>
        <Link to="/government/departments" className="wofy-link inline-block mt-4">← Back</Link>
      </div>
    </>
  );
  if (!d) return <><GovernmentCategoryNav /><div className="max-w-3xl mx-auto px-4 py-12 opacity-60 inline-flex gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div></>;

  const levelLabel = meta?.levels?.find(l => l.slug === d.level)?.label || d.level;
  const categoryLabel = meta?.department_categories?.find(c => c.slug === d.category)?.label;
  const isOwner = user && (d.owner_id === user.id || user.is_admin);

  return (
    <>
      <GovernmentCategoryNav />
      <article className="max-w-4xl mx-auto px-4 py-8" data-testid="dept-detail">
        {d.hero_image_url && (
          <div className="w-full h-56 sm:h-72 rounded-xl overflow-hidden mb-6 bg-black/5"
               style={{ backgroundImage: `url(${d.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}

        <div className="flex items-start gap-4 mb-4 flex-wrap">
          {d.logo_url ? (
            <img src={d.logo_url} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-white" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-black/5 grid place-items-center text-xl font-mono">
              {(d.name || "?")[0].toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="overline mb-1">
              <Landmark className="w-3 h-3 inline mr-1" />{levelLabel}
              {categoryLabel && <span className="opacity-70"> · {categoryLabel}</span>}
              {d.verified && <span className="ml-2 inline-flex items-center gap-0.5 text-green-600"><ShieldCheck className="w-3 h-3" />Verified</span>}
            </div>
            <h1 className="font-display text-4xl leading-tight">{d.name}</h1>
            {[d.city, d.district, d.state, d.country].filter(Boolean).length > 0 && (
              <div className="text-sm opacity-70 flex items-center gap-1 mt-1">
                <MapPin className="w-3.5 h-3.5" />
                {[d.city, d.district, d.state, d.country].filter(Boolean).join(", ")}
              </div>
            )}
          </div>
          {isOwner && (
            <Link to={`/websites/new?source_type=government_department&source_id=${d.id}`}
              className="wofy-btn-primary text-xs inline-flex items-center gap-1"
              data-testid="cta-instant-website">
              <Sparkles className="w-3.5 h-3.5" />Get a free website
            </Link>
          )}
        </div>

        {d.description && <p className="whitespace-pre-line leading-relaxed opacity-90 mb-6">{d.description}</p>}

        {d.responsibilities && (
          <section className="wofy-card !p-4 mb-6">
            <div className="overline mb-1">RESPONSIBILITIES</div>
            <p className="text-sm whitespace-pre-line opacity-90">{d.responsibilities}</p>
          </section>
        )}

        {/* Officials */}
        {d.officials?.length > 0 && (
          <section className="mb-8" data-testid="officials-section">
            <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2"><Users className="w-5 h-5" />Officials</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {d.officials.map(o => (
                <div key={o.id} className="wofy-card !p-3 flex items-center gap-3" data-testid={`official-${o.slug}`}>
                  {o.photo_url ? (
                    <img src={o.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-black/5 grid place-items-center text-sm font-mono">
                      {(o.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{o.name}</div>
                    <div className="text-xs opacity-70">{o.position}</div>
                    {o.office_email && <a href={`mailto:${o.office_email}`} className="text-xs wofy-link block truncate">{o.office_email}</a>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Notices */}
        {d.notices?.length > 0 && (
          <section className="mb-8" data-testid="notices-section">
            <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2"><Bell className="w-5 h-5" />Public notices</h2>
            <ul className="space-y-2">
              {d.notices.map(n => (
                <li key={n.id} className="wofy-card !p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">{n.kind.replace(/_/g, " ")}</div>
                  <div className="font-medium">{n.title}</div>
                  {n.summary && <p className="text-sm opacity-80 mt-1">{n.summary}</p>}
                  {n.valid_from && <div className="text-xs opacity-60 mt-1"><Clock className="w-3 h-3 inline mr-1" />{n.valid_from}{n.valid_until ? ` – ${n.valid_until}` : ""}</div>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Videos */}
        {d.video_links?.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2"><Video className="w-5 h-5" />Videos</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {d.video_links.map((v, i) => <VideoEmbed key={i} video={v} />)}
            </div>
          </section>
        )}

        {/* Contact + Ask */}
        <section className="grid gap-6 md:grid-cols-2 mb-8">
          {(d.email || d.phone || d.office_address || d.website) && (
            <div className="wofy-card !p-4" data-testid="contact-block">
              <div className="overline mb-2">CONTACT</div>
              {d.office_address && <div className="text-sm mb-1 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{d.office_address}</div>}
              <div className="text-sm space-y-1">
                {d.email && <div><a href={`mailto:${d.email}`} className="inline-flex items-center gap-1 wofy-link"><Mail className="w-3.5 h-3.5" />{d.email}</a></div>}
                {d.phone && <div><a href={`tel:${d.phone}`} className="inline-flex items-center gap-1 wofy-link"><Phone className="w-3.5 h-3.5" />{d.phone}</a></div>}
                {d.emergency_phone && <div className="text-red-700"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />Emergency: {d.emergency_phone}</div>}
                {d.website && <div><a href={d.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 wofy-link"><Globe className="w-3.5 h-3.5" />Website</a></div>}
                {d.office_hours && <div className="opacity-70">Hours: {d.office_hours}</div>}
              </div>
            </div>
          )}
          <CitizenMessageForm dept={d} meta={meta} />
        </section>
      </article>
    </>
  );
}

function CitizenMessageForm({ dept, meta }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    category: "roads", kind: "complaint", subject: "", description: "",
    citizen_name: user?.name || "", citizen_email: user?.email || "", citizen_phone: "",
    country: dept.country || "", state: dept.state || "", city: dept.city || "", public: false,
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to message this department."); return; }
    if (!form.subject.trim() || !form.description.trim() || !form.citizen_name.trim()) {
      toast.error("Subject, description and your name are required."); return;
    }
    setBusy(true);
    try {
      await http.post("/government/messages", { department_id: dept.id, ...form });
      toast.success("Message submitted. You can track it under 'My Messages'.");
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send.");
    } finally { setBusy(false); }
  };

  if (sent) return (
    <div className="wofy-card !p-4" style={{ background: "var(--wofy-surface)" }} data-testid="msg-sent">
      <div className="font-medium mb-1">Thanks — we've got your message.</div>
      <p className="text-sm opacity-80">
        Track its status any time under{" "}
        <Link to="/government/my-messages" className="wofy-link">My Messages →</Link>
      </p>
      <p className="text-xs opacity-60 mt-2">
        Note: Social Pulse forwards messages for transparency; it does not replace official emergency or statutory reporting channels.
      </p>
    </div>
  );

  return (
    <form onSubmit={submit} className="wofy-card !p-4" data-testid="msg-form">
      <div className="overline mb-2 inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />ASK YOUR GOVERNMENT</div>
      <div className="grid gap-2 sm:grid-cols-2 mb-2">
        <select value={form.category} onChange={e => set("category", e.target.value)}
          className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }} data-testid="msg-category">
          {meta?.message_categories?.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
        </select>
        <select value={form.kind} onChange={e => set("kind", e.target.value)}
          className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }} data-testid="msg-kind">
          {meta?.message_kinds?.map(k => <option key={k.slug} value={k.slug}>{k.label}</option>)}
        </select>
      </div>
      <input required placeholder="Subject" value={form.subject} onChange={e => set("subject", e.target.value)}
        className="wofy-input mb-2" data-testid="msg-subject" />
      <textarea required rows={4} placeholder="Describe your question or concern" value={form.description}
        onChange={e => set("description", e.target.value)} className="wofy-input mb-2" data-testid="msg-description" />
      <div className="grid gap-2 sm:grid-cols-2 mb-2">
        <input required placeholder="Your name" value={form.citizen_name} onChange={e => set("citizen_name", e.target.value)}
          className="wofy-input" data-testid="msg-name" />
        <input type="email" placeholder="Email (optional)" value={form.citizen_email} onChange={e => set("citizen_email", e.target.value)}
          className="wofy-input" data-testid="msg-email" />
      </div>
      <label className="text-xs opacity-80 inline-flex items-center gap-1.5 mb-3">
        <input type="checkbox" checked={form.public} onChange={e => set("public", e.target.checked)} data-testid="msg-public" />
        Make my request publicly visible (your name/email stay private)
      </label>
      <button disabled={busy} className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="msg-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Submit message
      </button>
      <p className="text-[10px] opacity-60 mt-2">
        Forwarding does not guarantee a government response and does not replace official emergency or statutory channels.
      </p>
    </form>
  );
}
